import { yupResolver } from '@hookform/resolvers/yup';
import { SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';

import {
  EventType,
  EventsQuery,
} from '../../../../apollo/__generated__/graphql';
import { useUpdateEventMutation } from '../../../hooks/useUpdateEventMutation';
import { EventFormInputs } from '../EventForm/EventForm';

const schema = yup
  .object({
    placeId: yup
      .string()
      .min(1, "Le lieu de l'événement est requis")
      .required("Le lieu de l'événement est requis"),
    name: yup.string().required("Le nom de l'événement est requis"),
    description: yup
      .string()
      .required("La description de l'événement est requise"),
    type: yup
      .mixed<EventType>()
      .oneOf(Object.values(EventType), "Le type de l'événement est invalide")
      .required("Le type de l'événement est requis"),
    checkAddress: yup
      .string()
      .required("L'adresse pour les chèques est requise"),
    lydiaLink: yup
      .string()
      .optional()
      .url('Le lien Lydia doit être une URL valide'),
    maxUsers: yup
      .number()
      .positive('Le nombre maximum de participants doit être positif')
      .required('Le nombre maxium de participants est requis'),
    startAt: yup.date().required('La date de début est requise'),
    endAt: yup.date().required('La date de fin est requise'),
  })
  .required();

export const useEditEventDrawer = (
  event: EventsQuery['events'][number],
  onClose: () => void,
) => {
  const { updateEvent } = useUpdateEventMutation(() => {
    onClose();
  });

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<EventFormInputs>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: event.name,
      description: event.description,
      type: event.type,
      startAt: event.startAt,
      endAt: event.endAt,
      lydiaLink: event.lydiaLink ?? undefined,
      checkAddress: event.checkAddress,
      maxUsers: event.maxUsers,
      placeId: event.place.id,
    },
  });

  const onValid: SubmitHandler<EventFormInputs> = async (data) => {
    updateEvent({
      eventId: event.id,
      ...data,
      maxUsers: data.type === EventType.BedroomOnly ? 0 : data.maxUsers,
    });
  };
  const onSubmit = handleSubmit(onValid);

  const startAtValue = getValues('startAt');
  const endAtValue = getValues('endAt');
  const type = getValues('type');

  const onStartAtChange = (date: Date | null) => {
    setValue('startAt', date ?? new Date());
  };

  const onEndAtChange = (date: Date | null) => {
    setValue('endAt', date ?? new Date(), {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
  };

  const onPlaceChange = (placeId: string | null) => {
    setValue('placeId', placeId ?? '', {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
  };

  const onTypeChange = (type: EventType) => {
    setValue('type', type, {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
  };

  return {
    register,
    errors,
    onSubmit,
    onStartAtChange,
    onEndAtChange,
    startAtValue,
    endAtValue,
    onPlaceChange,
    onTypeChange,
    type,
  };
};
